import { Project, ProjectFolder, User, UserRole } from '@clef/shared/types';
import MaterialTable, { Column } from '@material-table/core';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { Box, Menu, MenuItem, makeStyles } from '@material-ui/core';
import { PROJECT_TYPE_ICON_LOOKUP } from '@/components/ProjectCard/NewProjectCard';
import useStyles from '../styles';
import { IconButton, Typography } from '@clef/client-library';
import { useDatasetMediaDetailsQuery } from '@/serverStore/dataset';
import ProjectFallbackImagePng from '@/images/project/project_fallback_image.png';
import { useGetProjectStatsByOrgIdQuery } from '@/serverStore/projects';
import HomeWorkIcon from '@material-ui/icons/HomeWorkOutlined';
import LockIcon from '@material-ui/icons/LockOutlined';
import MoreVert from '@material-ui/icons/MoreVert';
import FolderIcon from '@material-ui/icons/Folder';
import MoreIconMenu, { MoreIconMenuItemKeys } from '@/components/ProjectCard/MoreIconMenu';
import { useSnackbar } from 'notistack';
import DeleteProjectDialog from '@/components/Dialogs/DeleteProjectDialog';
import AddUsersToProjectDialog from '@/components/AddUsersToProjectDialog/AddUsersToProjectDialog';
import EditProjectNameTextField from '@/components/Text/EditProjectNameTextField';
import EditFolderNameTextField from '@/components/Text/EditFolderNameTextField';

const useFolderMenuStyles = makeStyles(() => ({
  deleteText: {
    fontWeight: 'bold',
  },
}));

type ListItem = (Project & { itemType: 'project' }) | (ProjectFolder & { itemType: 'folder' });

const ProjectCover: React.FC<{ project: Project }> = props => {
  const { project } = props;

  const styles = useStyles();

  const { data: mediaDetails, error: mediaDetailsError } = useDatasetMediaDetailsQuery({
    datasetId: project.datasetId,
    mediaId: project.coverMediaId,
  });
  if (mediaDetailsError || !mediaDetails?.url) {
    return <img src={ProjectFallbackImagePng} className={styles.projectListItemCoverImage} />;
  }
  return <img src={mediaDetails.url} className={styles.projectListItemCoverImage} />;
};

const ProjectListView: React.FC<{
  projects: Project[];
  folders: ProjectFolder[];
  onProjectItemClick: (projectId: number) => void;
  onFolderClick: (folderId: number) => void;
  onFolderDelete?: (folder: ProjectFolder) => void;
  onFolderMove?: (folder: ProjectFolder) => void;
  onProjectMove?: (project: Project) => void;
  folderPathMap?: Map<number | null, string>;
  isSearching?: boolean;
}> = props => {
  const {
    projects,
    folders,
    onProjectItemClick,
    onFolderClick,
    onFolderDelete,
    onFolderMove,
    onProjectMove,
    folderPathMap,
    isSearching,
  } = props;
  const { data: statsById } = useGetProjectStatsByOrgIdQuery();
  const currentUser = useTypedSelector(state => state.login.user!);
  const users = useTypedSelector(state => state.user.users);
  const [moreIconAnchorEl, setMoreIconAnchorEl] = useState<HTMLElement | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingFolderName, setIsEditingFolderName] = useState(false);
  const [isInviteTeamMembersDialogOpen, setIsInviteTeamMembersDialogOpen] = useState(false);
  const [isDeleteProjectDialogOpen, setIsDeleteProjectDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<ProjectFolder | null>(null);
  const [folderMenuAnchorEl, setFolderMenuAnchorEl] = useState<HTMLElement | null>(null);
  const folderMenuStyles = useFolderMenuStyles();

  const handleDeleteProjectDialogClose = () => setIsDeleteProjectDialogOpen(false);

  const handleMoreIconMenuClick = (key: MoreIconMenuItemKeys) => {
    switch (key) {
      case MoreIconMenuItemKeys.rename:
        setIsEditingName(true);
        break;
      case MoreIconMenuItemKeys.invite:
        setIsInviteTeamMembersDialogOpen(true);
        break;
      case MoreIconMenuItemKeys.move:
        if (selectedProject) {
          onProjectMove?.(selectedProject);
        }
        break;
      case MoreIconMenuItemKeys.delete:
        setIsDeleteProjectDialogOpen(true);
        break;
    }
  };

  const userById = useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {} as Record<string, User>);
  }, [users]);

  const styles = useStyles();

  // Combine folders and projects, with folders first
  const listItems: ListItem[] = useMemo(() => {
    const folderItems: ListItem[] = folders.map(folder => ({
      ...folder,
      itemType: 'folder' as const,
    }));
    const projectItems: ListItem[] = projects.map(project => ({
      ...project,
      itemType: 'project' as const,
    }));
    return [...folderItems, ...projectItems];
  }, [folders, projects]);

  const isFolder = (item: ListItem): item is ProjectFolder & { itemType: 'folder' } =>
    item.itemType === 'folder';

  // Helper to keep folders always first regardless of sort direction
  const compareFoldersFirst = (
    a: ListItem,
    b: ListItem,
    sortDirection: 'asc' | 'desc' | undefined,
  ): number | null => {
    const dir = sortDirection === 'desc' ? -1 : 1;
    if (isFolder(a) && !isFolder(b)) return -1 * dir;
    if (!isFolder(a) && isFolder(b)) return 1 * dir;
    return null; // Both same type, continue with regular comparison
  };

  const columns: Column<ListItem>[] = [
    {
      title: t('Name'),
      field: 'name',
      width: 300,
      render: item => {
        if (isFolder(item)) {
          return (
            <Box display="flex" alignItems="center">
              <Box
                mr={4}
                className={styles.projectListItemCoverImage}
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ background: '#f5f5f5' }}
              >
                <FolderIcon style={{ fontSize: 32, color: '#9e9e9e' }} />
              </Box>
              <FolderIcon style={{ fontSize: 20, color: '#9e9e9e', marginRight: 8 }} />
              {isEditingFolderName && selectedFolder?.id === item.id ? (
                <EditFolderNameTextField
                  name={item.name}
                  folderId={item.id}
                  setIsEditingName={setIsEditingFolderName}
                />
              ) : (
                <Typography variant="body_semibold">{item.name}</Typography>
              )}
            </Box>
          );
        }
        return (
          <Box display="flex" alignItems="center">
            <Box mr={4}>
              <ProjectCover project={item} />
            </Box>
            <Box className={styles.projectIconContainer}>
              {item.labelType && PROJECT_TYPE_ICON_LOOKUP[item.labelType]}
            </Box>
            {isEditingName && selectedProject?.id === item.id ? (
              <EditProjectNameTextField
                name={item.name}
                projectId={item.id}
                setIsEditingName={setIsEditingName}
              />
            ) : (
              <Typography variant="body_semibold">{item.name}</Typography>
            )}
          </Box>
        );
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        return a.name.localeCompare(b.name);
      },
    },
    {
      title: t('Creator'),
      width: 150,
      render: item => {
        if (isFolder(item)) {
          const creator = item.createdById ? userById[item.createdById] : null;
          if (!creator) {
            return '-';
          }
          const creatorName = [creator.name, creator.lastName].filter(Boolean).join(' ');
          const displayName =
            creator.id === currentUser.id
              ? t('{{ownerName}} (You)', { ownerName: creatorName })
              : creatorName;
          return <Typography variant="body_semibold">{displayName}</Typography>;
        }
        const ownerId = item.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const owner = userById[ownerId];
        if (!owner) {
          return '-';
        }
        const ownerName = [owner.name, owner.lastName].filter(Boolean).join(' ');
        const displayName =
          owner.id === currentUser.id ? t('{{ownerName}} (You)', { ownerName }) : ownerName;
        return (
          <Box display="flex" alignItems="center">
            <Box mr={2}>
              {item.inviteOnly ? <LockIcon fontSize="small" /> : <HomeWorkIcon fontSize="small" />}
            </Box>
            <Typography variant="body_semibold">{displayName}</Typography>
          </Box>
        );
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        if (isFolder(a) && isFolder(b)) {
          return a.name.localeCompare(b.name);
        }
        const projA = a as Project & { itemType: 'project' };
        const projB = b as Project & { itemType: 'project' };
        const ownerIdA = projA.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const ownerIdB = projB.usersRoles?.find(user => user.role === 'owner')?.userId ?? '';
        const ownerA = userById[ownerIdA];
        const ownerB = userById[ownerIdB];
        const ownerNameA = [ownerA?.name, ownerA?.lastName].filter(Boolean).join(' ');
        const ownerNameB = [ownerB?.name, ownerB?.lastName].filter(Boolean).join(' ');
        if (!ownerNameA || !ownerNameB) {
          const emailA = ownerA?.email || '';
          const emailB = ownerB?.email || '';
          return emailA.localeCompare(emailB);
        }
        return ownerNameA.localeCompare(ownerNameB);
      },
    },
    // Location column - only shown when searching
    ...(isSearching && folderPathMap
      ? [
          {
            title: t('Location'),
            width: 200,
            render: (item: ListItem) => {
              if (isFolder(item)) {
                // For folders, show their parent folder path
                const parentPath = folderPathMap.get(item.parentFolderId) || t('Projects');
                return (
                  <Typography variant="body2" style={{ color: '#666' }}>
                    {parentPath}
                  </Typography>
                );
              }
              // For projects, show their folder path
              const project = item as Project & { itemType: 'project' };
              const folderPath =
                folderPathMap.get(project.projectFolderId ?? null) || t('Projects');
              return (
                <Typography variant="body2" style={{ color: '#666' }}>
                  {folderPath}
                </Typography>
              );
            },
            customSort: (a: ListItem, b: ListItem) => {
              const pathA = isFolder(a)
                ? folderPathMap.get(a.parentFolderId) || ''
                : folderPathMap.get((a as Project).projectFolderId ?? null) || '';
              const pathB = isFolder(b)
                ? folderPathMap.get(b.parentFolderId) || ''
                : folderPathMap.get((b as Project).projectFolderId ?? null) || '';
              return pathA.localeCompare(pathB);
            },
          } as Column<ListItem>,
        ]
      : []),
    {
      title: t('Images'),
      width: 80,
      render: item => {
        if (isFolder(item)) return '-';
        return statsById?.mediaCountByDatasetId[item.datasetId!] || 0;
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        if (isFolder(a) && isFolder(b)) return 0;
        const projA = a as Project & { itemType: 'project' };
        const projB = b as Project & { itemType: 'project' };
        const countA = statsById?.mediaCountByDatasetId[projA.datasetId!] || 0;
        const countB = statsById?.mediaCountByDatasetId[projB.datasetId!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Classes'),
      width: 80,
      render: item => {
        if (isFolder(item)) return '-';
        return statsById?.classCountByProjectId[item.id!] || 0;
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        if (isFolder(a) && isFolder(b)) return 0;
        const projA = a as Project & { itemType: 'project' };
        const projB = b as Project & { itemType: 'project' };
        const countA = statsById?.classCountByProjectId[projA.id!] || 0;
        const countB = statsById?.classCountByProjectId[projB.id!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Models'),
      width: 80,
      render: item => {
        if (isFolder(item)) return '-';
        return statsById?.modelCountByProjectId[item.id!] || 0;
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        if (isFolder(a) && isFolder(b)) return 0;
        const projA = a as Project & { itemType: 'project' };
        const projB = b as Project & { itemType: 'project' };
        const countA = statsById?.modelCountByProjectId[projA.id!] || 0;
        const countB = statsById?.modelCountByProjectId[projB.id!] || 0;
        return countA - countB;
      },
    },
    {
      title: t('Created'),
      width: 150,
      render: item => {
        return format(new Date(item.createdAt), 'MMM dd, yyyy');
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      },
    },
    {
      title: t('Last opened'),
      width: 150,
      render: item => {
        if (isFolder(item)) return '-';
        const owner = item.usersRoles?.find(user => user.role === 'owner');
        if (!owner?.lastOpenedTime) {
          return '-';
        }
        return format(new Date(owner.lastOpenedTime), 'MMM dd, yyyy');
      },
      customSort: (a, b, _type, sortDirection) => {
        const folderCompare = compareFoldersFirst(a, b, sortDirection);
        if (folderCompare !== null) return folderCompare;
        if (isFolder(a) && isFolder(b)) return 0;
        const projA = a as Project & { itemType: 'project' };
        const projB = b as Project & { itemType: 'project' };
        const ownerA = projA.usersRoles?.find(user => user.role === 'owner');
        const ownerB = projB.usersRoles?.find(user => user.role === 'owner');
        const lastOpenedA = new Date(ownerA?.lastOpenedTime || 0).getTime();
        const lastOpenedB = new Date(ownerB?.lastOpenedTime || 0).getTime();
        return lastOpenedA - lastOpenedB;
      },
    },
    {
      title: t('Actions'),
      width: 60,
      render: item => {
        const isCollaborator = currentUser.userRole === UserRole.Collaborator;
        if (isFolder(item)) {
          if (isCollaborator) {
            return null;
          }
          return (
            <Box display="flex" alignItems="center">
              <IconButton
                data-testid="folder_actions_button"
                aria-haspopup="true"
                onClick={e => {
                  e.stopPropagation();
                  setSelectedFolder(item);
                  setFolderMenuAnchorEl(e.currentTarget);
                }}
              >
                <MoreVert />
              </IconButton>
            </Box>
          );
        }
        const owner = item.usersRoles?.find(user => user.role === 'owner');
        const isCurrentUserOwner = owner?.userId === currentUser.id;
        if (!isCurrentUserOwner) {
          return null;
        }
        return (
          <Box display="flex" alignItems="center">
            <IconButton
              data-testid="fre_projects_actions_button"
              aria-controls="fre_projects_actions_menu"
              aria-haspopup="true"
              onClick={e => {
                e.stopPropagation();
                setSelectedProject(item);
                setMoreIconAnchorEl(e.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>
          </Box>
        );
      },
      sorting: false,
    },
  ];
  return (
    <>
      <MaterialTable
        columns={columns}
        data={listItems}
        options={{
          search: false,
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 25, 50, 100],
          toolbar: false,
        }}
        onRowClick={(event, rowData) => {
          if (!rowData) return;
          if (isFolder(rowData)) {
            onFolderClick(rowData.id);
          } else {
            onProjectItemClick(rowData.id);
          }
        }}
      />
      {selectedProject && (
        <>
          <MoreIconMenu
            anchorEl={moreIconAnchorEl}
            hideInvite={!selectedProject.inviteOnly}
            onClose={() => setMoreIconAnchorEl(null)}
            onBlur={() => setMoreIconAnchorEl(null)}
            onItemClick={(e, key) => {
              e.stopPropagation();
              setMoreIconAnchorEl(null);
              const owner = selectedProject.usersRoles?.find(user => user.role === 'owner');
              const isCurrentUserOwner = owner?.userId === currentUser.id;
              if (!isCurrentUserOwner) {
                enqueueSnackbar(t('Only project owner can perform this operation'), {
                  variant: 'info',
                });
                return;
              }
              handleMoreIconMenuClick(key);
            }}
          />
          <DeleteProjectDialog
            project={selectedProject}
            open={isDeleteProjectDialogOpen}
            handleClose={handleDeleteProjectDialogClose}
          />
          <AddUsersToProjectDialog
            projectId={selectedProject.id}
            open={isInviteTeamMembersDialogOpen}
            onCancel={() => setIsInviteTeamMembersDialogOpen(false)}
          />
        </>
      )}

      {/* Folder Menu */}
      <Menu
        anchorEl={folderMenuAnchorEl}
        open={Boolean(folderMenuAnchorEl)}
        onClose={() => setFolderMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            setFolderMenuAnchorEl(null);
            setIsEditingFolderName(true);
          }}
        >
          {t('Rename')}
        </MenuItem>
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            setFolderMenuAnchorEl(null);
            if (selectedFolder) {
              onFolderMove?.(selectedFolder);
            }
          }}
        >
          {t('Move to...')}
        </MenuItem>
        <MenuItem
          onClick={e => {
            e.stopPropagation();
            setFolderMenuAnchorEl(null);
            if (selectedFolder) {
              onFolderDelete?.(selectedFolder);
            }
          }}
        >
          <Typography color="secondary" className={folderMenuStyles.deleteText}>
            {t('Delete')}
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProjectListView;
